import React, { useEffect, useState } from 'react';
import { useContent } from '@/hooks/useContent';
import { submitContact } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import AnnualReportsSection from '@/components/AnnualReportsSection';
import { 
  Heart, Stethoscope, GraduationCap, AlertCircle, Activity, 
  Briefcase, UtensilsCrossed, Brain, Users, Droplet, 
  HeartHandshake, Ambulance 
} from 'lucide-react';
import '../public-style.css';

export default function PublicSite() {
  const { content } = useContent();
  const { toast } = useToast();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+91',
    phoneNumber: '',
    age: '',
    state: '',
    country: '',
    message: ''
  });

  useEffect(() => {
    const handleScroll = () => {
      const navbar = document.getElementById('navbar');
      if (window.scrollY > 50) {
        navbar?.classList.add('scrolled');
      } else {
        navbar?.classList.remove('scrolled');
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!content) return <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>;

  const site = content.site || {};
  const about = content.about || {};
  const values = about.coreValues || [];
  const stats = content.stats || [];
  const stories = content.testimonials || [];
  const services = content.services || [];
  const projects = content.projects || [];
  const annualReports = content.annualReports || [];

  // Icon mapping for services
  const iconMap = {
    Heart: Heart,
    Stethoscope: Stethoscope,
    GraduationCap: GraduationCap,
    AlertCircle: AlertCircle,
    Activity: Activity,
    Briefcase: Briefcase,
    UtensilsCrossed: UtensilsCrossed,
    Brain: Brain,
    Users: Users,
    Droplet: Droplet,
    HeartHandshake: HeartHandshake,
    Ambulance: Ambulance
  };

  const getServiceIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Heart;
    return <IconComponent size={32} />;
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleJoinSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const phone = `${formData.countryCode} ${formData.phoneNumber}`;
      const fullMessage = `Age: ${formData.age}\nLocation: ${formData.state}, ${formData.country}\n\nMessage:\n${formData.message}`;
      
      await submitContact({
        name: formData.name,
        email: formData.email,
        phone: phone,
        subject: 'Membership Application',
        message: fullMessage
      });
      
      toast({
        title: "Application Submitted",
        description: "Thank you! We will be in touch soon.",
      });
      setFormData({
        name: '', email: '', countryCode: '+91', phoneNumber: '', age: '', state: '', country: '', message: ''
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: err.message || "Failed to submit application",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <div className="public-site">
      <nav className="navbar" id="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <a href="#home" aria-label={`${site.brandName || 'Chinar NGO'} home`}>
              <img
                style={{ width: '2.8rem', verticalAlign: 'middle', padding: '5px' }}
                src="/images/logo.webp"
                alt="Logo"
                className="nav-logo-img"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </a>
          </div>

          <div className={`hamburger ${isMenuOpen ? 'active' : ''}`} id="hamburger" onClick={toggleMenu}>
            <span></span>
            <span></span>
            <span></span>
          </div>

          <ul className={`nav-menu ${isMenuOpen ? 'active' : ''}`} id="nav-menu">
            <li><a href="#home" className="nav-link" onClick={() => setIsMenuOpen(false)}>Home</a></li>
            <li><a href="#services" className="nav-link" onClick={() => setIsMenuOpen(false)}>Services</a></li>
            <li><a href="#projects" className="nav-link" onClick={() => setIsMenuOpen(false)}>Projects</a></li>
            <li><a href="#mission" className="nav-link" onClick={() => setIsMenuOpen(false)}>Mission</a></li>
            <li><a href="#impact" className="nav-link" onClick={() => setIsMenuOpen(false)}>Impact</a></li>
            <li><a href="#stories" className="nav-link" onClick={() => setIsMenuOpen(false)}>Stories</a></li>
            <li><a href="#annual-reports" className="nav-link" onClick={() => setIsMenuOpen(false)}>Annual Reports</a></li>
            <li><a href="#about" className="nav-link" onClick={() => setIsMenuOpen(false)}>About Us</a></li>
            <li><a href="#contact" className="nav-link" onClick={() => setIsMenuOpen(false)}>Join Us</a></li>
          </ul>
        </div>
      </nav>

      <section id="home" className="hero-section" style={{ backgroundImage: content.heroImage ? `url(${content.heroImage})` : 'none' }}>
        <div className="video-background">
          <video autoPlay muted loop playsInline id="hero-video">
            <source src="/videos/background.mp4" type="video/mp4" />
          </video>
          <div className="video-overlay"></div>
        </div>
        
        <div className="hero-content">
          <h1 className="hero-title">{site.brandName || 'Chinar Charity Foundation'}</h1>
          <p className="hero-subtitle">{site.tagline || 'Empowering Communities, Transforming Lives'}</p>
          <p className="hero-description">Together we can make a difference in the lives of those who need it most</p>
          <div className="hero-buttons">
            <a href="#contact" className="btn btn-primary">Join Our Mission</a>
            <a href="#projects" className="btn btn-secondary">View Projects</a>
          </div>
        </div>
      </section>

      <section id="projects" className="projects-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Projects</h2>
            <p className="section-subtitle">Making a Real Difference in Communities</p>
          </div>
          
          <div className="projects-grid">
            {projects.map((project, index) => (
              <div className="project-card" key={project.id || index}>
                <div className="project-image">
                  <img src={project.image || `https://images.unsplash.com/photo-1593113598332-cd288d649433?w=600&q=${index}`} alt={project.title} />
                </div>
                <div className="project-content">
                  <h3>{project.title}</h3>
                  <p>{project.description}</p>
                  {project.status && <span className={`project-status ${project.status.toLowerCase().replace(' ', '-')}`}>{project.status}</span>}
                  {project.progress !== undefined && (
                    <div className="project-progress">
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${project.progress}%` }}></div>
                      </div>
                      <span className="progress-text">{project.progress}%</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="services" className="services-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Services</h2>
            <p className="section-subtitle">Comprehensive Support for Communities in Need</p>
          </div>
          
          <div className="services-grid">
            {services.map((service, index) => (
              <div className="service-card" key={service.id || index}>
                <div className="service-icon">
                  {getServiceIcon(service.icon)}
                </div>
                <div className="service-content">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="mission" className="mission-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Mission</h2>
            <p className="section-subtitle">Dedicated to Creating Lasting Change</p>
          </div>
          
          <div className="mission-content">
            <div className="mission-text">
              <h3>{about.mission ? 'Our Mission Statement' : 'Building a Better Tomorrow'}</h3>
              <p>{about.mission || 'At Chinar Charity Foundation, we believe that every individual deserves access to basic necessities, education, and opportunities for growth.'}</p>
              
              <div className="mission-values">
                {values.slice(0, 4).map((val, i) => (
                  <div className="value-item" key={i}>
                    <i className={`fas fa-${val.icon?.toLowerCase() === 'heart' ? 'heart' : 'handshake'}`}></i>
                    <h4>{val.title}</h4>
                    <p>{val.description}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mission-image">
              <img src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=800" alt="Our Mission" />
            </div>
          </div>
        </div>
      </section>

      <section id="impact" className="impact-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Impact</h2>
            <p className="section-subtitle">Numbers That Tell Our Story</p>
          </div>
          
          <div className="impact-grid">
            {stats.slice(0, 4).map((stat, i) => (
              <div className="impact-card" key={i}>
                <div className="impact-icon">
                  <i className={`fas fa-${i === 0 ? 'child' : i === 1 ? 'hands-helping' : i === 2 ? 'project-diagram' : 'globe-asia'}`}></i>
                </div>
                <div className="impact-number">{stat.value}+</div>
                <div className="impact-label">{stat.label}</div>
              </div>
            ))}
          </div>
          
          <div className="impact-testimonial">
            <div className="testimonial-content">
              <div className="testimonial-image">
                <img src="https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=400" alt="Community Impact" />
              </div>
              <div className="testimonial-text">
                <h3>Creating Sustainable Change</h3>
                <p>"Our focus is not just on providing immediate relief, but on creating long-term sustainable solutions that empower communities to thrive independently. Every project we undertake is designed with sustainability and community involvement at its core."</p>
                <div className="testimonial-author">
                  <strong>Foundation Team</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="stories" className="stories-section">
        <div className="container">
          <div className="section-header">
            <h2>Success Stories</h2>
            <p className="section-subtitle">Real Stories, Real Impact</p>
          </div>
          
          <div className="stories-grid">
            {stories.slice(0, 3).map((story, i) => (
              <div className="story-card" key={i}>
                <div className="story-image">
                  <img src={story.avatar || `https://images.unsplash.com/photo-1544776193-352d25ca82cd?w=500&q=${i}`} alt="Success Story" />
                </div>
                <div className="story-content">
                  <h3>{story.name}'s Story</h3>
                  <p>"{story.text}"</p>
                  <span className="story-author">- {story.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="about" className="about-section">
        <div className="container">
          <div className="section-header">
            <h2>About Us</h2>
            <p className="section-subtitle">Who We Are and What We Stand For</p>
          </div>
          
          <div className="about-content">
            <div className="about-image">
              <img src="https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=700" alt="About Us" />
            </div>
            
            <div className="about-text">
              <h3>{about.story?.title || 'Our Story'}</h3>
              <p>{about.story?.intro || 'Founded with a vision to create meaningful change...'}</p>
              
              <div className="about-highlights">
                <div className="highlight-item">
                  <i className="fas fa-award"></i>
                  <div>
                    <h4>Recognized Excellence</h4>
                    <p>Award-winning programs and initiatives</p>
                  </div>
                </div>
                
                <div className="highlight-item">
                  <i className="fas fa-certificate"></i>
                  <div>
                    <h4>Certified NGO</h4>
                    <p>Registered and transparent operations</p>
                  </div>
                </div>
                
                <div className="highlight-item">
                  <i className="fas fa-network-wired"></i>
                  <div>
                    <h4>Strong Network</h4>
                    <p>Partnerships with local and international organizations</p>
                  </div>
                </div>
              </div>
              
              <div className="team-section">
                <h3>Our Team</h3>
                <p>Led by passionate individuals committed to social change, our team brings together diverse expertise in social work, education, healthcare, and community development.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <AnnualReportsSection annualReports={annualReports} />

      <section id="contact" className="contact-section">
        <div className="container">
          <div className="section-header">
            <h2>Join Our Mission</h2>
            <p className="section-subtitle">Be the Change You Want to See</p>
          </div>
          
          <div className="contact-content">
            <div className="contact-info">
              <h3>Get in Touch</h3>
              <p>Whether you want to volunteer, donate, or partner with us, we'd love to hear from you. Together, we can create lasting change.</p>
              
              <div className="info-items">
                <div className="info-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <div>
                    <h4>Address</h4>
                    <p>{site.address}</p>
                  </div>
                </div>
                
                <div className="info-item">
                  <i className="fas fa-phone"></i>
                  <div>
                    <h4>Phone</h4>
                    <p>{site.phone}</p>
                  </div>
                </div>
                
                <div className="info-item">
                  <i className="fas fa-envelope"></i>
                  <div>
                    <h4>Email</h4>
                    <p>{site.email}</p>
                  </div>
                </div>
                
                <div className="info-item">
                  <i className="fas fa-clock"></i>
                  <div>
                    <h4>Office Hours</h4>
                    <p>{site.officeHours?.[0] || 'Mon - Sat: 9:00 AM - 6:00 PM'}</p>
                  </div>
                </div>
              </div>
              
              <div className="social-links">
                {site.socials?.facebook && <a href={site.socials.facebook} className="social-link"><i className="fab fa-facebook"></i></a>}
                {site.socials?.twitter && <a href={site.socials.twitter} className="social-link"><i className="fab fa-twitter"></i></a>}
                {site.socials?.instagram && <a href={site.socials.instagram} className="social-link"><i className="fab fa-instagram"></i></a>}
                {site.socials?.youtube && <a href={site.socials.youtube} className="social-link"><i className="fab fa-youtube"></i></a>}
              </div>
            </div>
            
            <div className="contact-form">
              <h3>Become a Member</h3>
              <form id="joinForm" onSubmit={handleJoinSubmit}>
                <div className="form-row" style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="name" style={{ display: 'block', marginBottom: '5px' }}>Full Name *</label>
                    <input type="text" id="name" name="name" required value={formData.name} onChange={handleFormChange} style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }} />
                  </div>
                  
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="email" style={{ display: 'block', marginBottom: '5px' }}>Email Address *</label>
                    <input type="email" id="email" name="email" required value={formData.email} onChange={handleFormChange} style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }} />
                  </div>
                </div>
                
                <div className="form-row" style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="phone" style={{ display: 'block', marginBottom: '5px' }}>Phone Number *</label>
                    <div className="phone-input" style={{ display: 'flex', gap: '10px' }}>
                      <select id="countryCode" name="countryCode" required value={formData.countryCode} onChange={handleFormChange} style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}>
                        <option value="+91">+91 (India)</option>
                        <option value="+1">+1 (USA)</option>
                        <option value="+44">+44 (UK)</option>
                      </select>
                      <input type="tel" id="phoneNumber" name="phoneNumber" pattern="[0-9]{10}" required value={formData.phoneNumber} onChange={handleFormChange} style={{ flex: 1, padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }} />
                    </div>
                  </div>
                  
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="age" style={{ display: 'block', marginBottom: '5px' }}>Age *</label>
                    <input type="number" id="age" name="age" min="18" max="100" required value={formData.age} onChange={handleFormChange} style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }} />
                  </div>
                </div>
                
                <div className="form-row" style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="state" style={{ display: 'block', marginBottom: '5px' }}>State *</label>
                    <input type="text" id="state" name="state" required value={formData.state} onChange={handleFormChange} style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }} />
                  </div>
                  
                  <div className="form-group" style={{ flex: 1 }}>
                    <label htmlFor="country" style={{ display: 'block', marginBottom: '5px' }}>Country *</label>
                    <select id="country" name="country" required value={formData.country} onChange={handleFormChange} style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}>
                      <option value="">Select Country</option>
                      <option value="India">India</option>
                      <option value="USA">United States</option>
                      <option value="UK">United Kingdom</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label htmlFor="message" style={{ display: 'block', marginBottom: '5px' }}>Message *</label>
                  <textarea id="message" name="message" rows="5" required placeholder="Tell us why you want to join..." value={formData.message} onChange={handleFormChange} style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}></textarea>
                </div>
                
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>{site.brandName || 'Chinar Charity Foundation'}</h3>
              <p>{site.tagline}</p>
            </div>
            
            <div className="footer-section">
              <h4>Quick Links</h4>
              <ul>
                <li><a href="#home">Home</a></li>
                <li><a href="#mission">Mission</a></li>
                <li><a href="#projects">Projects</a></li>
                <li><a href="#impact">Impact</a></li>
              </ul>
            </div>
            
            <div className="footer-section">
              <h4>Get Involved</h4>
              <ul>
                <li><a href="#contact">Volunteer</a></li>
                <li><a href="#contact">Donate</a></li>
                <li><a href="#contact">Partner</a></li>
                <li><a href="#stories">Success Stories</a></li>
              </ul>
            </div>
            
            <div className="footer-section">
              <h4>Contact</h4>
              <ul>
                <li><i className="fas fa-phone"></i> {site.phone}</li>
                <li><i className="fas fa-envelope"></i> {site.email}</li>
                <li><i className="fas fa-map-marker-alt"></i> {site.address}</li>
              </ul>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} {site.brandName}. All rights reserved.</p>
            <p>Made with <i className="fas fa-heart"></i> for a better tomorrow</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
